import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { CalendarPlus, RotateCcw, Send } from "lucide-react";

import { libraryService, type Book, type BookCopy, type Borrowing, type EligibleStudent, type Fine, type Reservation } from "../../services/libraryService";
import { apiErrorMessage } from "../../utils/apiError";
import AIAssistField from "../AIAssistField";
import { Field } from "../FormLayout";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { ActionPanel, DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { IssueForm, ReserveForm } from "./CirculationForms";
import {
  CONDITIONS,
  FINE_TYPES,
  MAX_RENEWALS,
  day,
  fineConfig,
  fineStatus,
  isOpenReservation,
  isOut,
  labelFrom,
  loanConfig,
  loanStatus,
  money,
  overdueFineToday,
  renewalBlock,
  reservationConfig,
  reservationStatus,
  unpaidTotal,
} from "./libraryLogic";

const errorLine = (error: unknown, fallback: string) => (
  <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{apiErrorMessage(error, fallback)}</p>
);

// --- Loans ---------------------------------------------------------------------------------

/** Books out, due, overdue and back. Staff lend, take back, renew and record losses; families see their own. */
export function LoansPanel({ loans, copies, students, reservations, isLoading, isError, onRetry, canManage, onChanged , initialView}: {
  /** A register view to open on, e.g. "overdue" from the dashboard. */
  initialView?: string;
  loans?: Borrowing[];
  copies: BookCopy[];
  students: EligibleStudent[];
  reservations: Reservation[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canManage: boolean;
  onChanged: () => void;
}) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [issuing, setIssuing] = useState(false);
  const now = Date.now();
  const done = (text: string) => {
    setNotice({ tone: "success", text });
    onChanged();
  };
  const giveBack = useMutation({
    mutationFn: libraryService.returnBook,
    onSuccess: (loan) => {
      const fine = Number(loan.fine_amount);
      done(`${loan.book_title} is back from ${loan.borrower_name}.${fine ? ` A fine of ${money(fine)} was recorded.` : ""}`);
    },
  });
  const renew = useMutation({ mutationFn: libraryService.renew, onSuccess: (loan) => done(`${loan.book_title} renewed; now due ${day(loan.due_date)}.`) });
  const lose = useMutation({
    mutationFn: libraryService.reportLost,
    onSuccess: (loan) => {
      setViewing(null);
      done(`${loan.book_title} recorded as lost; a replacement fine was added for ${loan.borrower_name}.`);
    },
  });
  const reservedTitles = new Set(reservations.filter(isOpenReservation).map((row) => row.book_title));

  const columns: RegisterColumn<Borrowing>[] = [
    { header: "Book", cell: (row) => <span className="font-medium text-gray-900">{row.book_title}</span>, exportValue: (row) => row.book_title, className: "min-w-[14rem] whitespace-normal" },
    { header: "Borrower", cell: (row) => row.borrower_name, exportValue: (row) => row.borrower_name, className: "text-gray-700" },
    { header: "Borrowed", cell: (row) => day(row.borrow_date), exportValue: (row) => day(row.borrow_date), className: "tabular-nums text-gray-700" },
    { header: "Due", cell: (row) => day(row.due_date), exportValue: (row) => day(row.due_date), className: "tabular-nums text-gray-700" },
    { header: "Returned", cell: (row) => day(row.return_date) || <span className="text-gray-300">—</span>, exportValue: (row) => day(row.return_date), className: "tabular-nums text-gray-700" },
    { header: "Renewed", cell: (row) => `${row.renewal_count} of ${MAX_RENEWALS}`, exportValue: (row) => row.renewal_count, align: "right" },
    { header: "Status", cell: (row) => <StatusDot {...loanStatus(row, now)} />, exportValue: (row) => loanStatus(row, now).label },
  ];

  const loan = viewing !== null ? loans?.find((row) => row.id === viewing) : undefined;
  const blocked = loan ? renewalBlock(loan, reservedTitles, loan.book_title, now) : "";

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        key={initialView}
        initialState={initialView ? { view: initialView } : undefined}
        title={canManage ? "Loans" : "Books out"}
        description={canManage ? "Open a loan to take the book back, renew it or record it lost." : "What is out on loan, when it is due, and what has come back."}
        actions={canManage && (
          <button type="button" disabled={!copies.some((copy) => copy.status === "available")} onClick={() => setIssuing(true)} className="btn-primary h-8 min-h-8 px-3 text-xs disabled:opacity-50">
            <Send className="mr-1.5 h-3.5 w-3.5" /> Lend a book
          </button>
        )}
        rows={loans}
        config={loanConfig(now)}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => {
          giveBack.reset();
          renew.reset();
          lose.reset();
          setViewing(row.id);
        }}
        openLabel={(row) => (canManage && isOut(row) ? "Return / renew" : "Open")}
        noun={["loan", "loans"]}
        exportName={{ file: "library-loans", title: "Library Loans" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={canManage ? "No books have been lent yet." : "Nothing has been borrowed yet."}
      />

      {loan && (
        <DetailsModal
          title={loan.book_title}
          headline={{ label: isOut(loan) ? "Due back" : "Borrowed", value: isOut(loan) ? day(loan.due_date) : `${day(loan.borrow_date)} → ${day(loan.return_date)}`, note: loan.borrower_name }}
          status={loanStatus(loan, now)}
          facts={[
            ["Borrower", loan.borrower_name],
            ["Author", loan.book_authors],
            ["Borrowed", day(loan.borrow_date)],
            ["Due", day(loan.due_date)],
            ["Returned", day(loan.return_date) || undefined],
            ["Renewed", `${loan.renewal_count} of ${MAX_RENEWALS} times`],
            ["Lent by", loan.borrowed_by_name],
            ["Taken back by", loan.returned_by_name ?? undefined],
            ["Fine", Number(loan.fine_amount) ? `${money(loan.fine_amount)}${loan.fine_paid ? " (settled)" : ""}` : "None"],
          ]}
          notice={isOut(loan) && overdueFineToday(loan, now) > 0 ? (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
              Returned today, it carries an overdue fine of {money(overdueFineToday(loan, now))} ({money(1000)} a day).
            </p>
          ) : undefined}
          sections={[{ title: "Notes", body: loan.notes }]}
          onClose={() => setViewing(null)}
        >
          {canManage && isOut(loan) && (
            <>
              <ActionPanel title="Take it back" description="The copy goes back on the shelf, or to the next pupil waiting for it.">
                <form
                  onSubmit={(event: FormEvent<HTMLFormElement>) => {
                    event.preventDefault();
                    const data = new FormData(event.currentTarget);
                    giveBack.mutate({ borrowing_id: loan.id, condition: String(data.get("condition")), damage_fine: String(data.get("damage_fine") || "0") });
                  }}
                  className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end"
                >
                  <Field label="Condition now">
                    <select name="condition" defaultValue="good" className="input mt-1">
                      {CONDITIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  </Field>
                  <Field label="Damage fine (UGX)" optional>
                    <input type="number" min={0} step="any" name="damage_fine" defaultValue={0} className="input mt-1 tabular-nums" />
                  </Field>
                  <button disabled={giveBack.isPending} className="btn btn-primary gap-1.5"><RotateCcw className="h-4 w-4" /> {giveBack.isPending ? "Saving…" : "Take back"}</button>
                </form>
                {giveBack.isError && errorLine(giveBack.error, "The book could not be taken back.")}
              </ActionPanel>
              <ActionPanel title="Renew" description={blocked ? `It cannot be renewed: ${blocked}` : `Adds 14 days. ${MAX_RENEWALS - loan.renewal_count} ${MAX_RENEWALS - loan.renewal_count === 1 ? "renewal" : "renewals"} left.`}>
                <div className="flex justify-end">
                  <button type="button" disabled={Boolean(blocked) || renew.isPending} onClick={() => renew.mutate(loan.id)} className="btn btn-outline">Renew for 14 days</button>
                </div>
                {renew.isError && errorLine(renew.error, "The loan could not be renewed.")}
              </ActionPanel>
              <ActionPanel title="Record it lost" description="The loan closes and the pupil is fined the replacement cost.">
                <form
                  onSubmit={(event: FormEvent<HTMLFormElement>) => {
                    event.preventDefault();
                    const data = new FormData(event.currentTarget);
                    if (window.confirm(`Record ${loan.book_title} as lost by ${loan.borrower_name}? This closes the loan.`)) {
                      lose.mutate({ borrowing_id: loan.id, replacement_cost: String(data.get("replacement_cost")) });
                    }
                  }}
                  className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end"
                >
                  <Field label="Replacement cost (UGX)">
                    <input required type="number" min={1} step="any" name="replacement_cost" className="input mt-1 tabular-nums" />
                  </Field>
                  <button disabled={lose.isPending} className="rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50">Record lost</button>
                </form>
                {lose.isError && errorLine(lose.error, "The loss could not be recorded.")}
              </ActionPanel>
            </>
          )}
        </DetailsModal>
      )}

      {issuing && (
        <IssueForm
          students={students}
          copies={copies}
          onClose={() => setIssuing(false)}
          onDone={(text) => {
            setIssuing(false);
            done(text);
          }}
        />
      )}
    </div>
  );
}

// --- Reservations ------------------------------------------------------------------------

export function ReservationsPanel({ reservations, books, students, isLoading, isError, onRetry, canManage, canReserve, onChanged , initialView}: {
  /** A register view to open on, e.g. "overdue" from the dashboard. */
  initialView?: string;
  reservations?: Reservation[];
  books: Book[];
  students: EligibleStudent[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canManage: boolean;
  canReserve: boolean;
  onChanged: () => void;
}) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [reserving, setReserving] = useState(false);
  const done = (text: string) => {
    setViewing(null);
    setNotice({ tone: "success", text });
    onChanged();
  };
  const fulfil = useMutation({ mutationFn: libraryService.fulfillReservation, onSuccess: (loan) => done(`${loan.book_title} lent to ${loan.borrower_name}, due ${day(loan.due_date)}.`) });
  const cancel = useMutation({ mutationFn: libraryService.cancelReservation, onSuccess: (row) => done(`Reservation of ${row.book_title} for ${row.student_name} cancelled.`) });

  const columns: RegisterColumn<Reservation>[] = [
    { header: "Book", cell: (row) => <span className="font-medium text-gray-900">{row.book_title}</span>, exportValue: (row) => row.book_title, className: "min-w-[14rem] whitespace-normal" },
    { header: "For", cell: (row) => row.student_name, exportValue: (row) => row.student_name, className: "text-gray-700" },
    { header: "Queue", cell: (row) => (isOpenReservation(row) ? `No. ${row.priority}` : <span className="text-gray-300">—</span>), exportValue: (row) => row.priority, align: "right" },
    { header: "Reserved", cell: (row) => day(row.reservation_date), exportValue: (row) => day(row.reservation_date), className: "tabular-nums text-gray-700" },
    { header: "Held until", cell: (row) => day(row.expiry_date), exportValue: (row) => day(row.expiry_date), className: "tabular-nums text-gray-700" },
    { header: "Status", cell: (row) => <StatusDot {...reservationStatus(row)} />, exportValue: (row) => reservationStatus(row).label },
  ];
  const row = viewing !== null ? reservations?.find((item) => item.id === viewing) : undefined;

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        key={initialView}
        initialState={initialView ? { view: initialView } : undefined}
        title="Reservations"
        description={canManage ? "Who is waiting for which title. The first in the queue gets the next copy back." : "Books you are waiting for, and your place in the queue."}
        actions={canReserve && (
          <button type="button" disabled={!books.length} onClick={() => setReserving(true)} className="btn-primary h-8 min-h-8 px-3 text-xs disabled:opacity-50">
            <CalendarPlus className="mr-1.5 h-3.5 w-3.5" /> Reserve a book
          </button>
        )}
        rows={reservations}
        config={reservationConfig}
        columns={columns}
        rowKey={(item) => item.id}
        onOpen={(item) => {
          fulfil.reset();
          cancel.reset();
          setViewing(item.id);
        }}
        noun={["reservation", "reservations"]}
        exportName={{ file: "library-reservations", title: "Library Reservations" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No reservations."
      />
      {row && (
        <DetailsModal
          title={row.book_title}
          headline={{ label: "Place in the queue", value: isOpenReservation(row) ? `No. ${row.priority}` : "—", note: row.student_name }}
          status={reservationStatus(row)}
          facts={[
            ["For", row.student_name],
            ["Author", row.book_authors],
            ["Reserved", day(row.reservation_date)],
            ["Held until", day(row.expiry_date)],
            ["Collected", day(row.fulfillment_date) || undefined],
            ["Reserved by", row.reserved_by_name],
          ]}
          sections={[{ title: "Notes", body: row.notes }]}
          onClose={() => setViewing(null)}
          footer={
            <>
              {isOpenReservation(row) && (
                <button
                  type="button"
                  disabled={cancel.isPending}
                  onClick={() => window.confirm(`Cancel the reservation of ${row.book_title} for ${row.student_name}?`) && cancel.mutate(row.id)}
                  className="mr-auto rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                >
                  Cancel reservation
                </button>
              )}
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {canManage && isOpenReservation(row) && (
                <button type="button" disabled={fulfil.isPending} onClick={() => fulfil.mutate(row.id)} className="btn btn-primary gap-1.5">
                  <Send className="h-4 w-4" /> Lend it now
                </button>
              )}
            </>
          }
        >
          {fulfil.isError && errorLine(fulfil.error, "It could not be lent.")}
          {cancel.isError && errorLine(cancel.error, "The reservation could not be cancelled.")}
        </DetailsModal>
      )}
      {reserving && (
        <ReserveForm
          students={students}
          books={books}
          onClose={() => setReserving(false)}
          onDone={(text) => {
            setReserving(false);
            done(text);
          }}
        />
      )}
    </div>
  );
}

// --- Fines -----------------------------------------------------------------------------------

export function FinesPanel({ fines, isLoading, isError, onRetry, canManage, canWaive, onChanged , initialView}: {
  /** A register view to open on, e.g. "overdue" from the dashboard. */
  initialView?: string;
  fines?: Fine[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canManage: boolean;
  canWaive: boolean;
  onChanged: () => void;
}) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [reference, setReference] = useState("");
  const [reason, setReason] = useState("");
  const done = (text: string) => {
    setViewing(null);
    setNotice({ tone: "success", text });
    onChanged();
  };
  const pay = useMutation({ mutationFn: ({ id, ref }: { id: number; ref: string }) => libraryService.payFine(id, ref), onSuccess: (fine) => done(`${money(fine.amount)} from ${fine.student_name} recorded as paid.`) });
  const waive = useMutation({ mutationFn: ({ id, why }: { id: number; why: string }) => libraryService.waiveFine(id, why), onSuccess: (fine) => done(`${money(fine.amount)} for ${fine.student_name} waived.`) });

  const columns: RegisterColumn<Fine>[] = [
    { header: "Issued", cell: (row) => day(row.issued_date), exportValue: (row) => day(row.issued_date), className: "tabular-nums text-gray-700" },
    { header: "Pupil", cell: (row) => <span className="font-medium text-gray-900">{row.student_name}</span>, exportValue: (row) => row.student_name },
    { header: "Book", cell: (row) => row.book_title, exportValue: (row) => row.book_title, className: "min-w-[12rem] whitespace-normal text-gray-700" },
    { header: "Kind", cell: (row) => labelFrom(FINE_TYPES, row.fine_type), exportValue: (row) => labelFrom(FINE_TYPES, row.fine_type), className: "text-gray-700" },
    { header: "Amount", cell: (row) => money(row.amount), exportValue: (row) => Number(row.amount), align: "right" },
    { header: "Status", cell: (row) => <StatusDot {...fineStatus(row)} />, exportValue: (row) => fineStatus(row).label },
  ];
  const fine = viewing !== null ? fines?.find((row) => row.id === viewing) : undefined;
  const unpaid = unpaidTotal(fines ?? []);

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        key={initialView}
        initialState={initialView ? { view: initialView } : undefined}
        title="Fines"
        description={unpaid ? `${money(unpaid)} unpaid. Record the receipt number when one is paid.` : "Overdue, damage and lost-book fines."}
        rows={fines}
        config={fineConfig}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => {
          setReference("");
          setReason("");
          pay.reset();
          waive.reset();
          setViewing(row.id);
        }}
        openLabel={(row) => ((canManage || canWaive) && row.status === "pending" ? "Settle" : "Open")}
        noun={["fine", "fines"]}
        exportName={{ file: "library-fines", title: "Library Fines" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No library fines."
      />
      {fine && (
        <DetailsModal
          title={`${labelFrom(FINE_TYPES, fine.fine_type)} fine`}
          headline={{ label: "Amount", value: money(fine.amount), note: `${fine.student_name} · ${fine.book_title}` }}
          status={fineStatus(fine)}
          facts={[
            ["Pupil", fine.student_name],
            ["Book", fine.book_title],
            ["Issued", day(fine.issued_date)],
            ["Issued by", fine.issued_by_name],
            ["Paid", day(fine.paid_date) || undefined],
            ["Receipt", fine.payment_reference ?? undefined],
            ["Waived", fine.waived_date ? `${day(fine.waived_date)} by ${fine.waived_by_name ?? "leadership"}` : undefined],
          ]}
          sections={[
            { title: "Why it was charged", body: fine.reason },
            { title: "Why it was waived", body: fine.waiver_reason },
          ]}
          onClose={() => setViewing(null)}
        >
          {fine.status === "pending" && canManage && (
            <ActionPanel title="Record the payment" description="Enter the receipt or payment reference it was paid against.">
              <form
                onSubmit={(event: FormEvent<HTMLFormElement>) => {
                  event.preventDefault();
                  if (reference.trim()) pay.mutate({ id: fine.id, ref: reference.trim() });
                }}
                className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end"
              >
                <Field label="Receipt or payment reference">
                  <input required value={reference} onChange={(event) => setReference(event.target.value)} className="input mt-1" placeholder="e.g. RCT-2026-00412" />
                </Field>
                <button disabled={pay.isPending} className="btn btn-primary">Record paid</button>
              </form>
              {pay.isError && errorLine(pay.error, "The payment could not be recorded.")}
            </ActionPanel>
          )}
          {fine.status === "pending" && canWaive && (
            <ActionPanel title="Waive it" description="For school leadership. The reason stays on the pupil's record.">
              <Field as="div" label="Reason">
                <AIAssistField
                  rows={2}
                  value={reason}
                  onChange={(next) => setReason(next)}
                  placeholder="e.g. The book was damaged by a leaking roof in the class library."
                  context={{ key: "library.note", params: { kind: "fine", fine: fine.id } }}
                  draftLabel="Draft the reason"
                  draftHint="Reads the fine. Jot why first, and it writes it up."
                />
              </Field>
              <div className="flex justify-end">
                <button type="button" disabled={!reason.trim() || waive.isPending} onClick={() => waive.mutate({ id: fine.id, why: reason.trim() })} className="btn btn-outline">
                  Waive {money(fine.amount)}
                </button>
              </div>
              {waive.isError && errorLine(waive.error, "The fine could not be waived.")}
            </ActionPanel>
          )}
        </DetailsModal>
      )}
    </div>
  );
}
